<template>
  <div class="borrowerAttributeNaics">
    <BorrowerAttributeConstructor
      :errorText="errorText"
      :disclaimer="disclaimer"
    >
      <template v-slot:input>
        <div
            ref="input"
            class="marketplace-input form-floating new-naics-search autocomplete"
            :class="{'is-invalid': v$.code?.$invalid && v$.code?.$dirty, 'fresh': value === ''}"
        >
          <input class="form-control"
            placeholder="Industry"
            data-cy="industry_naics"
            :value="value"
            :class="[
              'autocomplete-input',
              { 'autocomplete-input-no-results': noResults },
              { 'autocomplete-input-focused': focused }
            ]"
            @focus="onFocus"
            @blur="onBlur"
          />
          <label class="type-400">
            <span>Industry</span>
          </label>
          <LendioBootstrapTooltip v-if="tooltipText">
            <span class="text-tooltip-icon position-absolute text-primary" data-bs-toggle="tooltip" data-bs-placement="right" :title="tooltipText">
              <fa-icon :icon="['fal', 'circle-info']"></fa-icon>
            </span>
          </LendioBootstrapTooltip>
          <ul class="autocomplete-result-list naics-search-loading"></ul>
          <ul
            v-if="noResults && focused && !loading"
            class="autocomplete-result-list"
            style="position: absolute; width: 100%; top: 100%;"
          >
            <li class="autocomplete-result no-results">
              Enter complete words for best results. For example “restaurant”
            </li>
          </ul>
        </div>
      </template>
    </BorrowerAttributeConstructor>
  </div>
</template>

<script>
import { useVuelidate } from '@vuelidate/core'
import env from '~/libs/env'
import get from 'lodash/get'
import forEach from 'lodash/forEach'
import { required } from '@vuelidate/validators'
import Autocompletejs from '@trevoreyre/autocomplete-js'
import BorrowerAttributeConstructor from './BorrowerAttributeConstructor.vue'
import AutocompleteCustomInput from '../AutocompleteCustomInput.vue'

export default {
  props: {
    question: Object,
    alias: String,
    disabled: Boolean,
    updateStoreValue: Function
  },
  emits: ['invalidity','answer'],
  setup: () => ({ v$: useVuelidate() }),
  data () {
    return {
      header: 'Industry NAICS',
      value: '',
      searchInput: '',
      autocomplete: null,
      focused: false,
      errorMessage: 'This field is invalid',
      data: [],
      code: this.question.value,
      loading: false,
      noResults: false
    }
  },
  components: {
    BorrowerAttributeConstructor,
    customInput: AutocompleteCustomInput
  },
  created () {
    this.$watch('invalid', (invalid) => {
      this.$emit('invalidity', invalid)
    }, {immediate: true})

    this.errorMessage = 'This field requires you to select an industry from the drop-down. Type in this field to show the drop-down.'
  },
  mounted () {
    if(this.question.value) {
      this.getIndustry(this.question.value)
    }

    this.autocomplete = new Autocompletejs(this.$refs.input, {
      search: this.getIndustries,
      getResultValue: this.getResultValue,
      onSubmit: this.onChange,
      // renderResult: this.renderResults,
      debounceTime: 500
    })
  },
  beforeUnmout() {
    // Clean up autocomplete
    if (this.autocomplete) {
      this.autocomplete.destroy()
    }
  },
  computed: {
    invalid() {
      return this.v$.$invalid
    },
    missingCode: function() {
      return !this.code
    },
    questionText() {
      return this.question.questionText || ''
    },
    description() {
      return this.question.description || ''
    },
    descriptionTooltip() {
      return this.question.descriptionTooltip || ''
    },
    label() {
      return this.question.name || ''
    },
    tooltipText() {
      return this.question.tooltip || this.question.helpText || ''
    },
    helpText() {
      return this.question.tooltip ? this.question.helpText : ''
    },
    errorText() {
      return get(this.v$, 'code.$error') ? this.errorMessage : ''
    },
    disclaimer() {
      return this.question.disclaimer || ''
    },
  },
  methods: {
    stringTitleCase(str) {
      return str.toLowerCase()
                .split(' ')
                .map(function (word) {
                  if (
                    word.match(/^and$/) ||
                    word.match(/^for$/) ||
                    word.match(/^of$/) ||
                    word.match(/^with$/) ||
                    word.match(/^the$/) ||
                    word.match(/^or$/)
                  ) {
                    return word
                  } else {
                    return word.replace(word[0], word[0].toUpperCase())
                  }
                })
                .join(' ')
    },
    async getIndustry(code) {
      await this.$axios
        .get(
          `${env(
            'apiUrl'
          )}/naics/find?endpoint=q&year=2012&code=${code}&terms=`
        )
        .then(res => {
          this.value = res.data.data.title
        })
        .catch(err => {
          console.log(err)
        })
    },
    async getIndustries(newQuery) {
      if (this.code && newQuery !== this.value) {
        this.code = null
      }
      this.value = newQuery

      if (newQuery.length < 1) {
        return []
      }
      return this.$axios.get(`${env('apiUrl')}/naics-search?terms=${newQuery}`).then(data => {
        let sortable = []
        if (data.data) {
          if (data.data.length === 0) {
            this.noResults = true
          } else {
          forEach(data.data, (o) => {
            this.noResults = false
            sortable.push({title: o.description.split(' : ').pop() + ' - ' + o.description_category, code: o.naics})
          })
          }
        }
        return sortable
      })
      .catch(err => {
        console.log(err)
        return []
      })
    },
    onBlur() {
      this.focused = false
      this.v$.$touch()
    },
    onFocus() {
      this.focused = true
    },
    onChange(result) {
      if (!result) {
        this.code = null;
        this.v$.code.$touch()
        this.$emit('answer', { value: null, isInvalid: true })
      } else {
        const capitalizedResult = result.title
          .toLowerCase()
          .split(' ')
          .map(function (word) {
            if (
              word.match(/^and$/) || word.match(/^for$/) || word.match(/^of$/) || word.match(/^with$/) || word.match(/^the$/) || word.match(/^or$/)
            ) {
              return word
            } else {
              return word.replace(word[0], word[0].toUpperCase())
            }
          })
          .join(' ')
        this.value = capitalizedResult
        let postValue = result.code
        this.code = result.code
        this.$emit('answer', { value: postValue, isInvalid: false })
      }
    },
    getResultValue(result) {
      return this.stringTitleCase(result.title)
    }
  },
  validations: function () {
    return {
      code: {
        required
      }
    }
  },
}
</script>

<style scoped lang="scss">
.autocomplete-result-list {
  z-index: 3 !important; // This is an inline style :( so the important is necessary
}

.new-naics-search {
  ul.dropdown-menu {
    cursor: pointer;
    li {
      cursor: pointer;
    }
  }

  .naics-search-loading {
    pointer-events: none;
    z-index: 1;
  }
}
</style>

<style lang="scss">
// NOT SCOPED
.autocomplete-input{border:1px solid #eee;border-radius:8px;width:100%;padding:12px 12px 12px 48px;box-sizing:border-box;position:relative;font-size:16px;line-height:1.5;flex:1;background-color:#eee;background-image:url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSIjNjY2IiBzdHJva2Utd2lkdGg9IjIiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCI+PGNpcmNsZSBjeD0iMTEiIGN5PSIxMSIgcj0iOCIvPjxwYXRoIGQ9Ik0yMSAyMWwtNC00Ii8+PC9zdmc+");background-repeat:no-repeat;background-position:12px}.autocomplete-input:focus,.autocomplete-input[aria-expanded=true]{border-color:rgba(0,0,0,.12);background-color:#fff;outline:none;box-shadow:0 2px 2px rgba(0,0,0,.16)}[data-position=below] .autocomplete-input[aria-expanded=true]{border-bottom-color:transparent;border-radius:8px 8px 0 0}[data-position=above] .autocomplete-input[aria-expanded=true]{border-top-color:transparent;border-radius:0 0 8px 8px;z-index:2}.autocomplete[data-loading=true]:after{content:"";border:3px solid rgba(0,0,0,.12);border-right-color:rgba(0,0,0,.48);border-radius:100%;width:20px;height:20px;position:absolute;right:12px;top:50%;transform:translateY(-50%);animation:rotate 1s linear infinite}.autocomplete-result-list{margin:0;border:1px solid rgba(0,0,0,.12);padding:0;box-sizing:border-box;max-height:296px;overflow-y:auto;background:#fff;list-style:none;box-shadow:0 2px 2px rgba(0,0,0,.16)}[data-position=below] .autocomplete-result-list{margin-top:-1px;border-top-color:transparent;border-radius:0 0 8px 8px;padding-bottom:8px}[data-position=above] .autocomplete-result-list{margin-bottom:-1px;border-bottom-color:transparent;border-radius:8px 8px 0 0;padding-top:8px}.autocomplete-result{cursor:default;padding:12px 12px 12px 48px;background-image:url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSIjY2NjIiBzdHJva2Utd2lkdGg9IjIiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCI+PGNpcmNsZSBjeD0iMTEiIGN5PSIxMSIgcj0iOCIvPjxwYXRoIGQ9Ik0yMSAyMWwtNC00Ii8+PC9zdmc+");background-repeat:no-repeat;background-position:12px}.autocomplete-result:hover,.autocomplete-result[aria-selected=true]{background-color:rgba(0,0,0,.06)}@keyframes rotate{0%{transform:translateY(-50%) rotate(0deg)}to{transform:translateY(-50%) rotate(359deg)}}

.new-naics-search {

  .autocomplete-input.autocomplete-input {
    border-radius: var(--bs-border-radius); // Keep the border radius normal
    background: none;
    padding-right: 2.25rem; // Padding to account for the tooltip and loading indicator
  }

  &.is-invalid input {
    border-color: #dc3545;
  }
  .autocomplete-result-list .autocomplete-result {
    padding-left: 12px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.12);
    transition: 0.3s ease background-color;
    padding-top: 10px;
    background: none;
    &:hover {
      background-color: $blue-100;
    }
    &:last-child {
      border-bottom: none;
    }
  }
  .autocomplete-result-list .autocomplete-result.no-results {
    &:hover {
      background-color: transparent;
    }
  }
  &[data-loading=true]:after {
    border-width: 2px;
    border-right: 2px solid rgb(18, 112, 237);
    background-color: #FFF;
    right: 14px;
  }

  .text-tooltip-icon {
    right:1rem;
    top:55%;
    transform:translateY(-50%);
  }

}
</style>
